/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
    Box,
    Card,
    Stack,
    Typography,
    Button,
    CircularProgress,
    Divider,
    Chip,
    Tabs,
    Tab,
    alpha,
} from '@mui/material';
import { Iconify } from '../../../components';
import { useSelector, dispatch } from '../../../redux/store';
// Use the canonical /form-deliveries/ slice (Track A.3 migration). The legacy
// `consentV2` slice still ships parallel reducers but hits the deprecated
// /consent-requests/ URL prefix. New code reads from `formDeliveries`.
import {
    getLeadConsentRequests,
    revokeConsentRequest,
    resendConsentRequest,
    getLeadFormSubmissions,
    loadMoreLeadFormSubmissions,
    clearLeadConsentRequests,
    clearLeadSubmissions,
    getConsentForms,
    getLeadArchivedRequests,
    clearLeadArchivedRequests,
} from '../../../redux/slices/formDeliveries';
// Fix #1: storage slice imports removed — the Files panel was reading
// cloud_storage::StorageFile, but form-submission PDFs live on
// ConsentRequest.pdf_file and were never bridged. Section is dropped until a
// Drive/Dropbox connector is wired up; the storage slice itself is still
// consumed by other surfaces.
import useSnackbar from '../../../hooks/useCustomSnackbar';
import FormRequestCard from './FormRequestCard';
import FormSubmissionCard from './FormSubmissionCard';
import SequenceStepper from './SequenceStepper';
import SendFormsDialog from '../../../components/dialogPopUp/SendFormsDialog';
import SubmissionViewerDialog from '../../../components/dialogPopUp/SubmissionViewerDialog';

// ─── Form-type taxonomy ────────────────────────────────────────────────────────
//
// The Forms tab is segregated by form-type group rather than by status. Patients
// see "I have a Medical History to fill in" as a unit; reps want to find the
// specific consent that's outstanding without scrolling. We collapse semantically
// related backend types (e.g. all consent variants) into a single user-facing
// group to keep the sub-tab strip readable.
//
// `match(formType)` decides which group a form falls under. `ALL` is a synthetic
// group that matches everything — kept first so the default view is unchanged
// for anyone who is muscle-memoried into seeing every form on the tab.
//
// TREATMENT_PLAN forms are intentionally NOT routed here — they live in the
// Treatments tab now.
// Fix #2: CALL_SCRIPT forms must also be excluded from the per-lead Forms tab
// (mirrors the filter in pages/dashboard/FormsPage.jsx line ~122). Call scripts
// are an outbound-call tool, not a patient-facing form, so they have no place
// in the lead's "All" view or the OTHER catch-all.
const FORM_GROUPS = [
    {
        key: 'ALL',
        label: 'All',
        icon: 'solar:document-text-bold-duotone',
        color: '#855CF8',
        match: (t) => t !== 'TREATMENT_PLAN' && t !== 'CALL_SCRIPT',
    },
    {
        key: 'CONSENT',
        label: 'Consent',
        icon: 'solar:document-text-bold-duotone',
        color: '#637381',
        match: (t) => ['CONSENT', 'COMMUNICATION_CONSENT', 'PHOTOGRAPHY_CONSENT', 'RADIOGRAPHY_CONSENT'].includes(t),
    },
    {
        key: 'MEDICAL_HISTORY',
        label: 'Medical History',
        icon: 'solar:health-bold-duotone',
        color: '#855CF8',
        match: (t) => ['MEDICAL_HISTORY', 'PATIENT_INTAKE'].includes(t),
    },
    {
        key: 'SURVEY',
        label: 'Surveys',
        icon: 'solar:chart-square-bold-duotone',
        color: '#26C6DA',
        match: (t) => t === 'SURVEY',
    },
    {
        key: 'OTHER',
        label: 'Other',
        icon: 'solar:document-bold-duotone',
        color: '#FFAB00',
        // Catch-all for everything that isn't a Treatment Plan or Call Script
        // and didn't match a first-class group above (e.g. REFERRAL, CUSTOM,
        // future types).
        match: (t) => t !== 'TREATMENT_PLAN'
            && t !== 'CALL_SCRIPT'
            && !['CONSENT', 'COMMUNICATION_CONSENT', 'PHOTOGRAPHY_CONSENT', 'RADIOGRAPHY_CONSENT'].includes(t)
            && !['MEDICAL_HISTORY', 'PATIENT_INTAKE'].includes(t)
            && t !== 'SURVEY',
    },
];

const formTypeOf = (item) => item?.form?.form_type || item?.form_type || 'CONSENT';

// ─── Sequence grouping helpers ────────────────────────────────────────────────

// Fix #5: memoize parseApiDate so we don't re-run moment parsing on every
// render for the same backend timestamp. Bounded LRU-lite — a Map preserves
// insertion order so we can drop the oldest entry once the cache fills. 1024
// entries is plenty for a single lead's request/submission set.
const PARSE_API_DATE_CACHE = new Map();
const PARSE_API_DATE_CACHE_MAX = 1024;

const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    if (PARSE_API_DATE_CACHE.has(dateStr)) {
        return PARSE_API_DATE_CACHE.get(dateStr);
    }
    const cleaned = dateStr.replace(/\.\d+/, '');
    const parsed = moment.utc(cleaned, 'DD-MM-YYYY HH:mm:ssZ');
    let result;
    if (parsed.isValid()) {
        result = parsed.local();
    } else {
        const iso = moment(dateStr);
        result = iso.isValid() ? iso : null;
    }
    if (PARSE_API_DATE_CACHE.size >= PARSE_API_DATE_CACHE_MAX) {
        // FIFO eviction — drop the first (oldest) key.
        const firstKey = PARSE_API_DATE_CACHE.keys().next().value;
        PARSE_API_DATE_CACHE.delete(firstKey);
    }
    PARSE_API_DATE_CACHE.set(dateStr, result);
    return result;
};

/**
 * Group consent requests into sequences.
 *
 * Priority:
 * 1. Backend `sequence_id` field (preferred — when backend supports it)
 * 2. `metadata.sequence_id` JSON field (stored by SendFormsDialog)
 *
 * Fix #3: the previous 90-second timestamp-proximity fallback created fake
 * sequences out of unrelated requests sent in the same minute. A request with
 * no `sequence_id` (top-level or in metadata) is now treated as standalone,
 * full stop. Only the two explicit branches above can produce a sequence.
 *
 * Singleton requests (no sequence partner) are returned in `standalone`.
 *
 * Returns: { sequences: [{id, name, created_at, isCompleted, steps}], standalone: Request[] }
 */
function groupIntoSequences(requests) {
    if (!requests || requests.length === 0) return { sequences: [], standalone: [] };

    const grouped = new Map(); // sequenceKey → requests[]
    const standalone = [];

    requests.forEach((req) => {
        // 1. Top-level sequence_id
        let seqKey = req.sequence_id || null;
        let seqName = req.sequence_name || null;

        // 2. Metadata JSON
        if (!seqKey && req.metadata) {
            try {
                const meta = typeof req.metadata === 'string' ? JSON.parse(req.metadata) : req.metadata;
                seqKey = meta.sequence_id || null;
                seqName = seqName || meta.sequence_name || null;
            } catch {
                // ignore parse error
            }
        }

        if (seqKey) {
            if (!grouped.has(seqKey)) grouped.set(seqKey, { key: seqKey, name: seqName, items: [] });
            grouped.get(seqKey).items.push(req);
        } else {
            // No sequence_id anywhere → standalone (fix #3).
            standalone.push(req);
        }
    });

    const sequences = [];

    grouped.forEach((group) => {
        const { items, name } = group;
        if (items.length <= 1) {
            // Solo — treat as standalone
            standalone.push(...items);
            return;
        }
        // Sort by sequence_position, then created_at
        const sorted = [...items].sort((a, b) => {
            const posA = a.sequence_position ?? 999;
            const posB = b.sequence_position ?? 999;
            if (posA !== posB) return posA - posB;
            const ta = parseApiDate(a.created_at);
            const tb = parseApiDate(b.created_at);
            if (ta && tb) return ta.valueOf() - tb.valueOf();
            return 0;
        });

        const isCompleted = sorted.every(
            (s) => s.status === 'SUBMITTED' || s.status === 'SKIPPED' || s.status === 'REVOKED'
        );

        // Fix #4: produce a sequence label the user can actually distinguish.
        // Preference order:
        //   1. BE-supplied sequence name (group.name)
        //   2. First step's form name, with " + N more" when more steps exist
        //   3. Fall back to "Form Sequence" only if both above are missing
        let resolvedName = name || null;
        if (!resolvedName) {
            const firstFormName = sorted[0]?.form?.name || null;
            if (firstFormName) {
                resolvedName = sorted.length > 1
                    ? `${firstFormName} + ${sorted.length - 1} more`
                    : firstFormName;
            }
        }

        sequences.push({
            id: group.key,
            name: resolvedName || 'Form Sequence',
            created_at: sorted[0]?.created_at,
            isCompleted,
            steps: sorted,
        });
    });

    // Sort sequences: most recent first; completed sequences go to the bottom
    sequences.sort((a, b) => {
        if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
        const ta = parseApiDate(a.created_at);
        const tb = parseApiDate(b.created_at);
        if (ta && tb) return tb.valueOf() - ta.valueOf();
        return 0;
    });

    return { sequences, standalone };
}

// Decide whether a sequence belongs to a given group. A sequence is shown under
// `groupKey` if any of its steps' form types match. The `ALL` group sees them
// all, except TREATMENT_PLAN and CALL_SCRIPT (fix #2 — same exclusion list as
// FORM_GROUPS.ALL.match).
const sequenceMatchesGroup = (sequence, groupKey) => {
    if (groupKey === 'ALL') {
        return sequence.steps.some((s) => {
            const t = formTypeOf(s);
            return t !== 'TREATMENT_PLAN' && t !== 'CALL_SCRIPT';
        });
    }
    const group = FORM_GROUPS.find((g) => g.key === groupKey);
    if (!group) return false;
    return sequence.steps.some((s) => group.match(formTypeOf(s)));
};

// ─────────────────────────────────────────────────────────────────────────────

export default function LeadFormsTab({ leadDetail }) {
    const { enqueueSnackbar } = useSnackbar();

    const practiceId = useSelector((state) => state.practiceTreatmentList.practiceId);
    const {
        leadConsentRequests,
        leadConsentLoading,
        leadSubmissions,
        leadSubmissionsLoading,
        leadArchivedRequests,
        leadArchivedLoading,
        leadArchivedLoaded,
    } = useSelector((state) => state.formDeliveries);
    // Fix #1: storage selectors removed alongside the Files section.

    const [activeGroup, setActiveGroup] = useState('ALL');
    const [sendFormOpen, setSendFormOpen] = useState(false);
    const [revoking, setRevoking] = useState(null);
    const [resending, setResending] = useState(null);
    const [viewingSubmission, setViewingSubmission] = useState(null);
    // Fix #6: track whether the "Load more" button is currently fetching so we
    // can show a spinner on it without flashing the global table loader.
    const [loadingMore, setLoadingMore] = useState(false);
    // Archived disclosure — collapsed by default; first expansion triggers the
    // lazy fetch via `getLeadArchivedRequests`.
    const [archivedOpen, setArchivedOpen] = useState(false);

    const leadId = leadDetail?.id;

    // Fix #6: pull a generous first page (matches the CR endpoint's limit=100
    // so the per-group filter on the sub-tabs sees the full set in the
    // typical case). The slim list serializer keeps the payload small even
    // at 100 rows; "Load more" is kept as edge-case insurance for the rare
    // patient with 100+ submissions, where per-group counts will then be
    // approximations until the user expands.
    //
    // Code-review fix: was 20, which broke per-group sub-tab counts when a
    // medical-history / survey submission sat beyond the first page.
    const SUBMISSIONS_PAGE_SIZE = 100;

    // ─── Load data ────────────────────────────────────────────────────────────
    const loadData = useCallback(() => {
        if (!practiceId || !leadId) return;
        dispatch(getLeadConsentRequests(practiceId, leadId));
        dispatch(getLeadFormSubmissions(practiceId, leadId, { limit: SUBMISSIONS_PAGE_SIZE, offset: 0 }));
        // Fix #1: getLeadFiles dispatch removed — Files panel is gone.
    }, [practiceId, leadId]);

    // Pre-fetch consent form templates so the SendForms picker loads instantly.
    // Treatment plan templates are no longer fetched here — the Treatments tab
    // owns that lifecycle now.
    useEffect(() => {
        if (!practiceId) return;
        dispatch(getConsentForms(practiceId, { limit: 50 }));
    }, [practiceId]);

    useEffect(() => {
        loadData();
        return () => {
            dispatch(clearLeadConsentRequests());
            dispatch(clearLeadSubmissions());
            dispatch(clearLeadArchivedRequests());
            // Fix #1: clearLeadFiles dispatch removed alongside the Files panel.
        };
    }, [loadData]);

    // Fix #6: "Load more" handler — fetches the next page using the current
    // results length as the offset, then appends them to leadSubmissions.
    const handleLoadMoreSubmissions = useCallback(() => {
        if (!practiceId || !leadId || loadingMore) return;
        const offset = (leadSubmissions.results || []).length;
        setLoadingMore(true);
        dispatch(loadMoreLeadFormSubmissions(practiceId, leadId, offset, SUBMISSIONS_PAGE_SIZE));
    }, [practiceId, leadId, leadSubmissions.results, loadingMore]);

    // Fix #6: drop the local loadingMore flag once the slice finishes the
    // append round-trip (leadSubmissionsLoading flips back to false).
    useEffect(() => {
        if (loadingMore && !leadSubmissionsLoading) {
            setLoadingMore(false);
        }
    }, [leadSubmissionsLoading, loadingMore]);

    // ─── Sequence grouping (across the whole list, then filtered per tab) ─────
    const { sequences: allSequences, standalone: allStandalone } = useMemo(
        () => groupIntoSequences(leadConsentRequests.results || []),
        [leadConsentRequests.results]
    );

    // Per-group filtered slices used to render the active sub-tab and to compute
    // the count chips on every sub-tab header.
    const sliceForGroup = useCallback((groupKey) => {
        const group = FORM_GROUPS.find((g) => g.key === groupKey);
        const matchType = group ? group.match : () => true;

        const sequences = allSequences.filter((s) => sequenceMatchesGroup(s, groupKey));
        const standalone = allStandalone.filter((r) => matchType(formTypeOf(r)));
        const submissions = (leadSubmissions.results || []).filter((s) => matchType(formTypeOf(s)));

        const standaloneActive = standalone.filter(
            (r) => r.status !== 'SUBMITTED' && r.status !== 'REVOKED'
        );
        const standaloneSubmitted = standalone.filter((r) => r.status === 'SUBMITTED');

        const count = sequences.length + standaloneActive.length + submissions.length + standaloneSubmitted.length;

        return { sequences, standaloneActive, standaloneSubmitted, submissions, count };
    }, [allSequences, allStandalone, leadSubmissions.results]);

    const groupCounts = useMemo(
        () => Object.fromEntries(FORM_GROUPS.map((g) => [g.key, sliceForGroup(g.key).count])),
        [sliceForGroup]
    );

    const active = sliceForGroup(activeGroup);
    const activeGroupMeta = FORM_GROUPS.find((g) => g.key === activeGroup) || FORM_GROUPS[0];

    // ─── Archived grouping (pure — same pipeline as the live list) ───────────
    const { sequences: archivedAllSequences, standalone: archivedAllStandalone } = useMemo(
        () => groupIntoSequences(leadArchivedRequests?.results || []),
        [leadArchivedRequests]
    );

    // Parallel helper that filters the archived requests by the active sub-tab.
    // Archived shows everything flat — no active/completed split — so it just
    // returns one merged list of sequences + standalone for the chosen group.
    const sliceArchivedForGroup = useCallback((groupKey) => {
        const group = FORM_GROUPS.find((g) => g.key === groupKey);
        const matchType = group ? group.match : () => true;
        const sequences = archivedAllSequences.filter((s) => sequenceMatchesGroup(s, groupKey));
        const standalone = archivedAllStandalone.filter((r) => matchType(formTypeOf(r)));
        const count = sequences.length + standalone.length;
        return { sequences, standalone, count };
    }, [archivedAllSequences, archivedAllStandalone]);

    const archivedActive = sliceArchivedForGroup(activeGroup);

    // ─── Actions ──────────────────────────────────────────────────────────────
    const handleCopyLink = useCallback((request) => {
        const link = request.form_link || request.link || '';
        if (link) {
            navigator.clipboard.writeText(link)
                .then(() => enqueueSnackbar('Link copied to clipboard', { variant: 'success' }))
                .catch(() => enqueueSnackbar('Could not copy link', { variant: 'error' }));
        } else {
            enqueueSnackbar('No link available', { variant: 'warning' });
        }
    }, [enqueueSnackbar]);

    const handleRevoke = useCallback((request) => {
        setRevoking(request.id);
        dispatch(revokeConsentRequest(practiceId, request.id, ({ success }) => {
            setRevoking(null);
            if (success) {
                enqueueSnackbar('Request revoked', { variant: 'success' });
                dispatch(getLeadConsentRequests(practiceId, leadId));
            } else {
                enqueueSnackbar('Failed to revoke request', { variant: 'error' });
            }
        }));
    }, [practiceId, leadId, enqueueSnackbar]);

    const handleResend = useCallback((request) => {
        setResending(request.id);
        dispatch(resendConsentRequest(practiceId, request.id, ({ success }) => {
            setResending(null);
            if (success) {
                enqueueSnackbar('Form resent successfully', { variant: 'success' });
                dispatch(getLeadConsentRequests(practiceId, leadId));
            } else {
                enqueueSnackbar('Failed to resend form', { variant: 'error' });
            }
        }));
    }, [practiceId, leadId, enqueueSnackbar]);

    const handleRemind = useCallback((request) => {
        // "Remind" = resend the specific form link
        handleResend(request);
    }, [handleResend]);

    // handleViewSubmission removed — all surfaces now route through SubmissionViewerDialog (handleViewSubmissionFull).

    const handleViewSubmissionFull = useCallback((submission) => {
        setViewingSubmission(submission);
    }, []);

    // PDF URL fallback chain — mirrors the one in FormSubmissionsPage. The
    // signed PDF is persisted onto ``ConsentRequest.pdf_file`` at submit time
    // (mirrored from the client-rendered upload, or from the reportlab
    // fallback via ``generate_submission_pdf``). The submission-scoped
    // endpoint may surface the URL under any of half a dozen names depending
    // on which serializer the back-end picked, and the linked CR is often
    // nested under ``submission.consent_request`` or available on the
    // sibling ``standaloneSubmitted`` rows. Trying every realistic key in
    // turn means the button stops dead-ending when only one of them is
    // populated.
    const handleDownloadPdf = useCallback((submission) => {
        const cr = submission?.consent_request || submission?.consent_requests?.[0] || null;
        const pdfUrl = submission?.pdf_file_url
            || submission?.pdf_url
            || submission?.pdf_file
            || cr?.pdf_file_url
            || cr?.pdf_url
            || cr?.pdf_file
            || null;
        if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener noreferrer');
        else enqueueSnackbar('PDF not yet available for this submission', { variant: 'info' });
    }, [enqueueSnackbar]);

    const handleSendFormClose = (refreshNeeded) => {
        setSendFormOpen(false);
        if (refreshNeeded) loadData();
    };

    // Toggle the Archived disclosure. On the first expand, kick off the lazy
    // fetch; subsequent opens reuse cached results.
    const handleToggleArchived = useCallback(() => {
        setArchivedOpen((prev) => {
            const next = !prev;
            if (next && !leadArchivedLoaded && !leadArchivedLoading && practiceId && leadId) {
                dispatch(getLeadArchivedRequests(practiceId, leadId));
            }
            return next;
        });
    }, [leadArchivedLoaded, leadArchivedLoading, practiceId, leadId]);

    // Fix #6: suppress the global loading spinner during an append round-trip
    // (loadingMore). Only the inline "Load more" button spinner should run.
    const isLoading = leadConsentLoading || (leadSubmissionsLoading && !loadingMore);
    const hasAnythingInActive = active.count > 0;
    const hasMoreSubmissions = (leadSubmissions.count || 0) > (leadSubmissions.results || []).length;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: 3 }}>
            {/* ══════════════════════════════════════════════════
                HEADER + SEND ACTION
            ══════════════════════════════════════════════════ */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon={activeGroupMeta.icon} width={20} sx={{ color: activeGroupMeta.color }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '14px' }}>
                        Forms
                    </Typography>
                </Stack>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<Iconify icon="eva:plus-fill" width={14} />}
                    onClick={() => setSendFormOpen(true)}
                    sx={{
                        bgcolor: '#855CF8',
                        '&:hover': { bgcolor: '#6B42E0' },
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '12px',
                    }}
                >
                    Send Form
                </Button>
            </Stack>

            {/* ══════════════════════════════════════════════════
                SUB-TABS BY FORM-TYPE GROUP
            ══════════════════════════════════════════════════ */}
            <Box sx={{ borderBottom: '1px solid', borderColor: 'rgba(145,158,171,0.16)', mb: 2 }}>
                <Tabs
                    value={activeGroup}
                    onChange={(e, val) => setActiveGroup(val)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 36,
                        '& .MuiTab-root': {
                            minHeight: 36,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '12px',
                            minWidth: 'auto',
                            px: 1.5,
                        },
                        '& .Mui-selected': { color: '#855CF8' },
                        '& .MuiTabs-indicator': { backgroundColor: '#855CF8' },
                    }}
                >
                    {FORM_GROUPS.map((g) => (
                        <Tab
                            key={g.key}
                            value={g.key}
                            label={
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <span>{g.label}</span>
                                    {groupCounts[g.key] > 0 && (
                                        <Chip
                                            label={groupCounts[g.key]}
                                            size="small"
                                            sx={{
                                                height: 16,
                                                fontSize: '10px',
                                                fontWeight: 700,
                                                bgcolor: alpha(activeGroup === g.key ? '#855CF8' : '#637381', 0.1),
                                                color: activeGroup === g.key ? '#855CF8' : '#637381',
                                                '& .MuiChip-label': { px: 0.75 },
                                            }}
                                        />
                                    )}
                                </Stack>
                            }
                        />
                    ))}
                </Tabs>
            </Box>

            {/* ══════════════════════════════════════════════════
                ACTIVE GROUP CONTENT — IN-PROGRESS → PENDING → COMPLETED
            ══════════════════════════════════════════════════ */}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} sx={{ color: '#855CF8' }} />
                </Box>
            ) : hasAnythingInActive ? (
                <>
                    {/* ── Active Sequences ── */}
                    {(() => {
                        const activeSeqs = active.sequences.filter((s) => !s.isCompleted);
                        const completedSeqs = active.sequences.filter((s) => s.isCompleted);
                        return (
                            <>
                                {activeSeqs.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                            <Iconify icon="solar:list-bold-duotone" width={14} sx={{ color: '#855CF8' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                Active Sequences
                                            </Typography>
                                            <Chip label={activeSeqs.length} size="small" sx={{ height: 16, fontSize: '10px', fontWeight: 700, bgcolor: alpha('#855CF8', 0.1), color: '#855CF8' }} />
                                        </Stack>
                                        <Stack spacing={1.5}>
                                            {activeSeqs.map((seq) => (
                                                <SequenceStepper
                                                    key={seq.id}
                                                    sequence={seq}
                                                    onCopyLink={handleCopyLink}
                                                    onRevoke={handleRevoke}
                                                    onResend={handleResend}
                                                    onRemind={handleRemind}
                                                    onViewSubmission={handleViewSubmissionFull}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}

                                {/* ── Completed Sequences ── */}
                                {completedSeqs.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        {activeSeqs.length > 0 && <Divider sx={{ mb: 2.5 }} />}
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                            <Iconify icon="eva:checkmark-circle-2-fill" width={14} sx={{ color: '#00AB55' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                Completed Sequences
                                            </Typography>
                                            <Chip label={completedSeqs.length} size="small" sx={{ height: 16, fontSize: '10px', fontWeight: 700, bgcolor: 'rgba(0,171,85,0.1)', color: '#00AB55' }} />
                                        </Stack>
                                        <Stack spacing={1.5}>
                                            {completedSeqs.map((seq) => (
                                                <SequenceStepper
                                                    key={seq.id}
                                                    sequence={seq}
                                                    onCopyLink={handleCopyLink}
                                                    onRevoke={handleRevoke}
                                                    onResend={handleResend}
                                                    onRemind={handleRemind}
                                                    onViewSubmission={handleViewSubmissionFull}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                            </>
                        );
                    })()}

                    {/* ── Standalone active requests ── */}
                    {active.standaloneActive.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            {active.sequences.length > 0 && <Divider sx={{ mb: 2.5 }} />}
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                <Iconify icon="eva:clock-fill" width={14} sx={{ color: '#FFAB00' }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                    Pending
                                </Typography>
                                <Chip label={active.standaloneActive.length} size="small" sx={{ height: 16, fontSize: '10px', fontWeight: 700, bgcolor: 'rgba(255,171,0,0.1)', color: '#FFAB00' }} />
                            </Stack>
                            <Stack spacing={1}>
                                {active.standaloneActive.map((request) => (
                                    <Box
                                        key={request.id}
                                        sx={{
                                            opacity: revoking === request.id || resending === request.id ? 0.6 : 1,
                                            transition: 'opacity 0.2s',
                                            pointerEvents: revoking === request.id || resending === request.id ? 'none' : 'auto',
                                        }}
                                    >
                                        <FormRequestCard
                                            request={request}
                                            onCopyLink={handleCopyLink}
                                            onRevoke={handleRevoke}
                                            onResend={handleResend}
                                            onViewSubmission={handleViewSubmissionFull}
                                        />
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* ── Completed (submissions + submitted standalone requests) ── */}
                    {(active.submissions.length > 0 || active.standaloneSubmitted.length > 0) && (
                        <Box sx={{ mb: 3 }}>
                            {(active.sequences.length > 0 || active.standaloneActive.length > 0) && <Divider sx={{ mb: 2.5 }} />}
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                <Iconify icon="eva:checkmark-circle-2-fill" width={14} sx={{ color: '#00AB55' }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                    Completed
                                </Typography>
                                <Chip
                                    label={active.submissions.length + active.standaloneSubmitted.length}
                                    size="small"
                                    sx={{ height: 16, fontSize: '10px', fontWeight: 700, bgcolor: 'rgba(0,171,85,0.1)', color: '#00AB55' }}
                                />
                            </Stack>
                            <Stack spacing={1}>
                                {active.submissions.map((submission) => (
                                    <FormSubmissionCard
                                        key={`sub-${submission.id}`}
                                        submission={submission}
                                        onView={handleViewSubmissionFull}
                                        onDownloadPdf={handleDownloadPdf}
                                    />
                                ))}
                                {active.standaloneSubmitted.map((request) => (
                                    <FormRequestCard
                                        key={`req-${request.id}`}
                                        request={request}
                                        onViewSubmission={handleViewSubmissionFull}
                                    />
                                ))}
                            </Stack>
                            {/* Fix #6: Load more — appears below Completed
                                whenever the server reports more submissions
                                than we currently hold in state. */}
                            {hasMoreSubmissions && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleLoadMoreSubmissions}
                                        disabled={loadingMore}
                                        startIcon={
                                            loadingMore
                                                ? <CircularProgress size={12} sx={{ color: '#855CF8' }} />
                                                : <Iconify icon="eva:arrow-ios-downward-fill" width={14} />
                                        }
                                        sx={{
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            borderColor: alpha('#855CF8', 0.5),
                                            color: '#855CF8',
                                            '&:hover': { bgcolor: alpha('#855CF8', 0.06), borderColor: '#855CF8' },
                                        }}
                                    >
                                        {loadingMore ? 'Loading…' : `Load more (${(leadSubmissions.count || 0) - (leadSubmissions.results || []).length} remaining)`}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </>
            ) : (
                /* ── Empty state ── */
                <Card
                    sx={{
                        py: 5,
                        textAlign: 'center',
                        borderRadius: '12px',
                        border: '1px dashed rgba(145,158,171,0.24)',
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                    }}
                >
                    <Iconify icon={activeGroupMeta.icon} width={40} sx={{ color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, fontSize: '13px' }}>
                        {activeGroup === 'ALL'
                            ? 'No forms sent yet'
                            : `No ${activeGroupMeta.label.toLowerCase()} forms yet`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '12px' }}>
                        {activeGroup === 'ALL'
                            ? 'Send a consent form, medical history, or survey to this patient'
                            : `Send a ${activeGroupMeta.label.toLowerCase()} form to this patient`}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Iconify icon="eva:plus-fill" width={14} />}
                            onClick={() => setSendFormOpen(true)}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '12px',
                                borderColor: '#855CF8',
                                color: '#855CF8',
                                '&:hover': { bgcolor: alpha('#855CF8', 0.06) },
                            }}
                        >
                            Send Form
                        </Button>
                    </Box>
                </Card>
            )}

            {/* Fix #1: Files section removed. Form-submission PDFs live on
                ConsentRequest.pdf_file but the old panel was reading from
                cloud_storage::StorageFile (never bridged) so it was always
                empty in practice. Re-add once a Drive/Dropbox connector
                lands. */}

            {/* ══════════════════════════════════════════════════
                ARCHIVED DISCLOSURE — lazy-loaded, always visible
            ══════════════════════════════════════════════════ */}
            <Box sx={{ mt: 2 }}>
                <Button
                    onClick={handleToggleArchived}
                    size="small"
                    startIcon={
                        <Iconify
                            icon={archivedOpen ? 'eva:chevron-down-fill' : 'eva:chevron-right-fill'}
                            width={16}
                        />
                    }
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '12px',
                        color: 'text.secondary',
                        '&:hover': { bgcolor: alpha('#855CF8', 0.06), color: '#855CF8' },
                    }}
                >
                    {leadArchivedLoaded
                        ? `Archived (${leadArchivedRequests?.count || 0})`
                        : 'Archived'}
                </Button>

                {archivedOpen && (
                    <Box sx={{ mt: 1.5 }}>
                        {leadArchivedLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={20} sx={{ color: '#855CF8' }} />
                            </Box>
                        ) : leadArchivedLoaded && archivedActive.count === 0 ? (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '12px', display: 'block', py: 1, pl: 1 }}>
                                No archived forms
                            </Typography>
                        ) : leadArchivedLoaded ? (
                            <Stack spacing={1.5}>
                                {archivedActive.sequences.map((seq) => (
                                    <SequenceStepper
                                        key={`archived-${seq.id}`}
                                        sequence={seq}
                                        onCopyLink={handleCopyLink}
                                        onRevoke={handleRevoke}
                                        onResend={handleResend}
                                        onRemind={handleRemind}
                                        onViewSubmission={handleViewSubmissionFull}
                                    />
                                ))}
                                {archivedActive.standalone.map((request) => (
                                    <FormRequestCard
                                        key={`archived-req-${request.id}`}
                                        request={request}
                                        onCopyLink={handleCopyLink}
                                        onRevoke={handleRevoke}
                                        onResend={handleResend}
                                        onViewSubmission={handleViewSubmissionFull}
                                    />
                                ))}
                            </Stack>
                        ) : null}
                    </Box>
                )}
            </Box>

            {/* Send form dialog */}
            {sendFormOpen && (
                <SendFormsDialog
                    open={sendFormOpen}
                    onClose={handleSendFormClose}
                    practiceId={practiceId}
                    initialLead={leadDetail}
                />
            )}

            {/* Submission viewer dialog */}
            <SubmissionViewerDialog
                open={!!viewingSubmission}
                submission={viewingSubmission}
                practiceId={practiceId}
                onClose={() => setViewingSubmission(null)}
            />
        </Box>
    );
}

LeadFormsTab.propTypes = {
    leadDetail: PropTypes.object.isRequired,
};
